/**
 * Reading what the user wants from a sentence typed beside an attachment.
 *
 * This exists because of a reported failure: the user attached a folder, typed
 * **"then convert this to 4 classes"**, and was told to go and do it in Project
 * Management. `convert` was not in the verb list, so the sentence read as a
 * question and went to the chat path.
 *
 * Three structural lessons are built in, and each is worth more than the word
 * that prompted it:
 *
 * 1. **A count plus a class noun IS an instruction**, with no verb at all.
 *    "4 classes", "4 classes me krdo", "4개 클래스로" carry no verb, and this is
 *    how people actually type once a file is attached. This rule alone would
 *    have caught the reported bug even with every verb list wrong.
 *
 * 2. **`ambiguous` is the fallback, never `question`.** Anything imperative but
 *    under-specified means ASK ONE QUESTION. Routing an unrecognised
 *    instruction to the chat is precisely the reported bug, and no verb list is
 *    ever going to be complete.
 *
 * 3. **Word boundaries do not exist in Hangul or Devanagari.** `\b` is a
 *    transition across `[A-Za-z0-9_]`, so `/\b만들/` and `/\bबांट/` match
 *    nothing, in any string, ever. Each intent therefore keeps one regex per
 *    script. The inverse is a happy accident worth knowing: `/\bclasses\b/`
 *    DOES match inside "4 classes로", because `로` is a non-word character.
 *
 * The old screen had a keyword gate that was deleted for being wrong in both
 * directions — `'ai'` matched inside "chair", `'personal'` blocked PPE
 * detection. That is what GUARDS below are for, and why the whole thing is
 * pinned by a 138-phrase corpus rather than by intuition.
 */

/** Phrases whose match is a false positive of some stem below. */
const GUARDS = [
  /\bbananas?\b/i, /\bsandbox\b/i, /\bcheckbox\b/i, /\bboxing\b/i,
  /\bclass(?:room|mate|ic|ical)\b/i, /\b(?:world|first)-?class\b/i,
  /\bsort\s+of\b/i, /\bkind\s+of\b/i, /\bsame\b/i, /\bsamples?\b/i,
  /\bmarket(?:ing)?\b/i, /\bgroup\s+(?:chat|call)\b/i,
];

const CLASS_NOUN =
  '(?:classes|class|clases|categories|category|groups|group|buckets|bucket|clusters|cluster|labels|label|types|type|kinds|kind|parts|part|folders|folder|hisse|hisso|tukde|개|가지|클래스|카테고리|분류|그룹|종류|क्लास|श्रेणी|वर्ग|हिस्स)';

const NUMBER_WORDS = Object.freeze({
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  ek: 1, do: 2, teen: 3, tin: 3, char: 4, chaar: 4, paanch: 5, panch: 5,
  chhe: 6, che: 6, cheh: 6, saat: 7, aath: 8, nau: 9, das: 10, dus: 10,
  하나: 1, 둘: 2, 셋: 3, 넷: 4, 다섯: 5, 여섯: 6, 일곱: 7, 여덟: 8, 아홉: 9, 열: 10,
  한: 1, 두: 2, 세: 3, 네: 4,
});

/** One test per script. See lesson 3 above. */
/*
 * NOTE ON THE MISSING TRAILING \b.
 *
 * These are PREFIX stems, and the boundary belongs only at the front. Written
 * `\b(?:detect|organi[sz])\b` the alternation matches "detect" and "organis"
 * but NOT "detection" or "organise" — the trailing boundary demands a non-word
 * character exactly where the suffix continues. That silently cost six of the
 * corpus's phrasings, including "mujhe helmet detection chahiye".
 */
const VERBS = {
  build: {
    latin: /\b(?:creat|build|built|mak(?:e|es|ing)|made|set\s*-?\s*up|setup|generat|import|ingest|initiali[sz]|spin\s*up|kick\s*off|prepar)|\b(?:banao|banado|banade|bnado|bnao|bnade|banaa|banakar|banake|chahiye)\b|\bbana(?!na)|\bbna\b|\bready\s*(?:kar|kr)\b/i,
    hangul: /(?:만들|만드|생성|제작|구축|셋업|세팅|넣어|올려)/,
    devanagari: /(?:बना|तैयार\s*कर)/,
  },
  divide: {
    latin: /\b(?:divid|split|separat|segregat|sort(?!\s+of)|group|re-?group|cluster|bucket|categori[sz]|classif|organi[sz]|arrange|tidy|clean\s*up|breakdown|chunk|partition|bifurcate|divvy|re-?split|convert|conversion|turn\s*(?:it|this|these|them)?\s*into|chang(?:e|ing)\s*(?:it|this)?\s*(?:to|into)|reduc(?:e|ing)\s*(?:it|this)?\s*to|merg(?:e|ing)|combin|collaps|break\s*(?:it|this|them|these)?\s*(?:down|up|into))|\b(?:baant|bant|baato|banto|alag|chhaant|chaant|badal)\b/i,
    hangul: /(?:나눠|나누|나눌|분류|구분|그룹|묶|정리|분리|바꿔|바꾸|변환|전환|갈라|쪼개)/,
    devanagari: /(?:बांट|बाँट|अलग|बदल|वर्गीकृत|छांट|समूह)/,
  },
  boxes: {
    latin: /\b(?:detect|locali[sz]|yolo|bound(?:ing)?\s*box|bbox|b-box)|\bboxe?s?\b|\bmark(?!et)/i,
    hangul: /(?:박스|바운딩|검출|탐지|네모|사각형|찾아|잡아)/,
    devanagari: /(?:बॉक्स|डिब्ब)/,
  },
  segment: {
    latin: /\b(?:segment|semantic\s*seg|instance\s*seg|panoptic|masks?|polygons?|contours?|outlin|pixel[\s-]*(?:level|wise)|per[\s-]*pixel|cut\s*out|cutout|silhouette|fastsam)/i,
    hangul: /(?:세그|마스크|폴리곤|외곽선|윤곽|픽셀\s*단위|영역\s*분할|오려)/,
    devanagari: /(?:मास्क|खंड|रूपरेखा)/,
  },
  /** Imperative but says nothing about WHICH task. */
  ambiguous: {
    latin: /\b(?:label|labels|labeling|labelling|labelled|labeled|annotate|annotation|auto\s*-?\s*label)\b/i,
    hangul: /(?:라벨|레이블|어노테이션)/,
    devanagari: /(?:लेबल)/,
  },
};

const QUESTION_HEADS = {
  latin: /^\s*(?:what|which|who|why|how|when|where|is|are|am|was|were|do\s+i|does|did|can|could|should|shall|would|will|may|might|tell\s*me|explain|recommend|suggest)\b/i,
  // Hinglish question words appear mid-sentence as often as at the start.
  hinglish: /\b(?:kya|kyaa|kyu|kyun|kaise|kese|kaisa|kaisi|kitna|kitne|kitni|kaun|kaunsa|konsa|kab|kahan|batao|bataiye|sahi\s*(?:hai|hoga|rahega|rahenge)|theek\s*(?:hai|rahega)|behtar|chahiye\s*kya|hoga\s*kya|honi\s*chahiye|kar\s*du|kr\s*du|fark|farak)\b/i,
  hangul: /(?:무엇|뭐|뭔|무슨|어떻|어떤|어느|왜|언제|어디|몇|얼마|될까|할까|좋을까|괜찮을까|나을까|충분|추천|차이|알려\s*줘|설명|맞나|인가요|나요|까요)/,
  devanagari: /(?:क्या|कितन|कैसे|कौन|क्यों|कहाँ|कब|बताओ)/,
};

const squash = (text) => String(text || '').replace(/\s+/g, ' ').trim();

/** Blank out guarded spans so a stem cannot match inside one. */
const withoutGuards = (text) => {
  let out = text;
  GUARDS.forEach((guard) => {
    out = out.replace(new RegExp(guard.source, `${guard.flags.replace('g', '')}g`), (match) =>
      ' '.repeat(match.length));
  });
  return out;
};

const hits = (set, masked, raw) =>
  (set.latin && set.latin.test(masked)) ||
  (set.hangul && set.hangul.test(raw)) ||
  (set.devanagari && set.devanagari.test(raw));

const isQuestionShaped = (masked, raw) =>
  QUESTION_HEADS.latin.test(masked) ||
  QUESTION_HEADS.hinglish.test(masked) ||
  QUESTION_HEADS.hangul.test(raw) ||
  QUESTION_HEADS.devanagari.test(raw);

const clampCount = (value) => {
  const number = Math.round(Number(value));
  if (!Number.isFinite(number) || number < 1) return 0;
  return Math.min(12, Math.max(2, number));
};

/**
 * The number of classes the sentence asks for, and whether it was a range.
 *
 * Four shapes, because all four are typed: `4 classes`, `four classes`,
 * `2-4 classes`, and a bare number after into/to/me/로 with no noun at all.
 */
export const readClassCount = (text) => {
  const raw = squash(text);
  const masked = withoutGuards(raw);
  const lower = masked.toLowerCase();

  const range = lower.match(new RegExp(`(\\d+)\\s*(?:-|–|~|to|se)\\s*(\\d+)\\s*${CLASS_NOUN}`, 'i'));
  if (range) {
    return { count: 0, maxCount: clampCount(Math.max(Number(range[1]), Number(range[2]))) };
  }

  const digits = lower.match(new RegExp(`(\\d+)\\s*${CLASS_NOUN}`, 'i'));
  if (digits) return { count: clampCount(digits[1]), maxCount: 0 };

  const words = lower.match(
    new RegExp(`\\b(${Object.keys(NUMBER_WORDS).join('|')})\\s*${CLASS_NOUN}`, 'i'),
  );
  if (words) return { count: clampCount(NUMBER_WORDS[words[1].toLowerCase()]), maxCount: 0 };

  const hangulCount = raw.match(new RegExp(`(\\d+)\\s*${CLASS_NOUN}`));
  if (hangulCount) return { count: clampCount(hangulCount[1]), maxCount: 0 };

  // English puts the number after the preposition ("into 3"); Hinglish and
  // Korean put it before the postposition ("3 me", "3으로").
  const bare = lower.match(/\b(?:into|in\s*to|to)\s*(\d+)\b/i) ||
    lower.match(/\b(\d+)\s*(?:me|mein|mai)\b/i) ||
    raw.match(/(\d+)\s*(?:로|으로|개)/);
  if (bare) return { count: clampCount(bare[1]), maxCount: 0 };

  // "make it 4" — a bare number attached to a pronoun, no class noun in sight.
  const pronoun = lower.match(/\b(?:it|this|that|them|these|isko|isse|ise)\s+(\d+)\b/i);
  if (pronoun) return { count: clampCount(pronoun[1]), maxCount: 0 };

  // A Korean counter word: "두 개" is two of them.
  const counter = raw.match(/(하나|둘|셋|넷|다섯|여섯|일곱|여덟|아홉|열|한|두|세|네)\s*개/);
  if (counter) return { count: clampCount(NUMBER_WORDS[counter[1]]), maxCount: 0 };

  return { count: 0, maxCount: 0 };
};

const OBJECT_SEPARATORS = /\s*(?:,|\band\b|\baur\b|&|\/|\+|이랑|랑|와|과|그리고)\s*/i;
const OBJECT_NOISE =
  /\b(?:with|using|please|the|all|every|each|karo|kar\s*do|kr\s*do|kro|krna|karna|ko|se|do|de|dena|chahiye|hai|images?|photos?|model)\b|[해줘|주세요|줘]+$/gi;

const splitObjects = (text) =>
  String(text || '')
    .split(OBJECT_SEPARATORS)
    .map((part) => part.replace(OBJECT_NOISE, '').replace(/[.!?]+$/, '').trim())
    .filter((part) => part && part.length <= 40 && !/^\d+$/.test(part))
    .slice(0, 12);

/**
 * Object names in a detection/segmentation instruction.
 *
 * Looks on BOTH sides of the verb, because word order differs by language:
 * English puts the objects after it ("detect person and helmet"), while Hindi
 * and Korean put them before ("person aur helmet detect krna hai",
 * "사람이랑 헬멧 검출해줘"). Reading only the tail finds nothing in two of the
 * three languages this product is used in.
 */
export const readObjects = (text) => {
  const raw = squash(text);
  const VERB = '(?:detect|find|locate|spot|boxes?\\s*(?:on|around|for)|box\\s*(?:on|around)|segment|mask|outline|mark|검출|탐지|찾아|구분|분류)';

  const tail = raw.match(new RegExp(`${VERB}\\s+(?:the\\s+|all\\s+|every\\s+|each\\s+)?(.+)$`, 'i'));
  const fromTail = tail ? splitObjects(tail[1]) : [];
  if (fromTail.length >= 2) return fromTail;

  const head = raw.match(new RegExp(`^(.+?)\\s*${VERB}`, 'i'));
  const fromHead = head ? splitObjects(head[1]) : [];
  if (fromHead.length >= 2) return fromHead;

  // A single fragment found BEFORE the verb is almost always sentence noise
  // ("I need object" out of "I need object detection on this"), whereas a
  // single fragment after it is usually the real thing ("boxes around the
  // people"). So the head is only trusted when it lists more than one.
  return fromTail.length ? fromTail : [];
};

const nameAfter = (text, keyword) => {
  const match = text.match(new RegExp(`\\b${keyword}\\s+(?:called|named|")?\\s*([\\w][\\w -]{0,58})`, 'i'));
  return match ? squash(match[1]) : null;
};

/**
 * Read an instruction.
 *
 * Returns `{intent, count, maxCount, objects, projectName, modelName}`.
 * `intent` is one of build | divide | boxes | segment | question | ambiguous.
 */
export const readIntent = (instruction) => {
  const raw = squash(instruction);
  const masked = withoutGuards(raw);
  const { count, maxCount } = readClassCount(raw);
  const objects = readObjects(raw);
  const base = {
    count, maxCount, objects,
    projectName: nameAfter(raw, 'project'),
    modelName: nameAfter(raw, 'model'),
  };

  // An empty message beside an attachment means "do the obvious thing".
  if (!raw) return { ...base, intent: 'build' };

  const asksSegment = hits(VERBS.segment, masked, raw);
  const asksBoxes = hits(VERBS.boxes, masked, raw);
  const asksDivide = hits(VERBS.divide, masked, raw);
  const asksBuild = hits(VERBS.build, masked, raw);
  const asksLabel = hits(VERBS.ambiguous, masked, raw);
  // Rule 1: a count beside a class noun needs no verb at all.
  const countIsInstruction = count > 0 || maxCount > 0;

  const question = isQuestionShaped(masked, raw) || /\?\s*$/.test(raw);
  // A PROPOSAL needs an imperative verb: "split this into 3?" is deciding out
  // loud. A count alone does not make one — "is 4 classes enough?" and "4개
  // 클래스면 충분할까요?" are asking for advice, and answering them by creating
  // a project would be the mirror of the bug this file exists to fix.
  const proposes = asksSegment || asksBoxes || asksDivide || asksBuild;

  if (question) {
    return { ...base, intent: proposes ? 'ambiguous' : 'question' };
  }

  // Naming the classes states how many there are: "cats and dogs" is two.
  const named = objects.length >= 2 && !count ? { ...base, count: objects.length } : base;

  if (asksSegment) return { ...named, intent: 'segment' };
  if (asksBoxes) return { ...named, intent: 'boxes' };
  if (asksDivide || countIsInstruction) return { ...named, intent: 'divide' };
  if (asksBuild) {
    // "make it 4" is imperative but says nothing about what four MEANS.
    return { ...named, intent: count && !/[a-z\u3131-\uD79D]{3,}/i.test(raw.replace(/\d+/g, '')) ? 'ambiguous' : 'build' };
  }
  if (asksLabel) return { ...named, intent: 'ambiguous' };

  // Rule 2: never fall through to chat. An unrecognised sentence beside an
  // attachment is a question we have not learned to read — so ask one.
  return { ...base, intent: 'ambiguous' };
};

export default readIntent;
